/**
 * Conversation metadata key: display decrypt, wrap/unwrap, and HKDF migration.
 *
 * One random 32-byte key per conversation protects the name, member settings,
 * read-state, and system events. The key is static-wrapped to
 * attested, pinned devices. HKDF(conversationId) remains decrypt-only for
 * blobs written before this key existed.
 *
 * @module services/conversationMetadataService
 */

import {
  compareObjectIdHex,
  isValidObjectIdHex,
  stubSystemEvent,
  type PublicConversation,
  type PublicMessage,
  type SerializedWrappedKey,
  type SystemEvent,
} from '@adieuu/shared';
import {
  encryptReadState,
  encryptReadStateWithKey,
  decryptReadStateWithKey,
  decryptReadState,
} from '@adieuu/crypto';
import {
  decryptGroupName,
  decryptGroupNameWithKey,
  decryptMemberSettings,
  decryptMemberSettingsWithKey,
  encryptGroupName,
  encryptMemberSettings,
  encryptSystemEvent,
  decryptSystemEvent,
  generateConversationMetadataKey,
  unwrapConversationMetadataKey,
  wrapConversationMetadataKey,
  type MemberSettingsMap,
  type RecipientKeys,
} from './conversationCryptoService';

export function conversationHasWrappedMetadata(
  conv: Pick<PublicConversation, 'wrappedMetadataKeys'>,
): boolean {
  return (conv.wrappedMetadataKeys?.length ?? 0) > 0;
}

export function decryptConversationNameForDisplay(
  conv: Pick<PublicConversation, 'id' | 'encryptedName' | 'nameNonce' | 'wrappedMetadataKeys'>,
  metadataKey?: Uint8Array,
): string | undefined {
  if (!conv.encryptedName || !conv.nameNonce) return undefined;
  if (metadataKey) {
    try {
      return decryptGroupNameWithKey(conv.encryptedName, conv.nameNonce, conv.id, metadataKey);
    } catch {
      // Leftover HKDF ciphertext: recover when this device holds the metadata key.
    }
    try {
      return decryptGroupName(conv.encryptedName, conv.nameNonce, conv.id);
    } catch {
      return undefined;
    }
  }
  if (conversationHasWrappedMetadata(conv)) return undefined;
  try {
    return decryptGroupName(conv.encryptedName, conv.nameNonce, conv.id);
  } catch {
    return undefined;
  }
}

export function decryptConversationMemberSettingsForDisplay(
  conv: Pick<
    PublicConversation,
    'id' | 'encryptedMemberSettings' | 'memberSettingsNonce' | 'wrappedMetadataKeys'
  >,
  metadataKey?: Uint8Array,
): MemberSettingsMap | undefined {
  if (!conv.encryptedMemberSettings || !conv.memberSettingsNonce) return undefined;
  if (metadataKey) {
    try {
      return decryptMemberSettingsWithKey(
        conv.encryptedMemberSettings,
        conv.memberSettingsNonce,
        conv.id,
        metadataKey,
      );
    } catch {
      // Leftover HKDF ciphertext: recover when this device holds the metadata key.
    }
    try {
      return decryptMemberSettings(
        conv.encryptedMemberSettings,
        conv.memberSettingsNonce,
        conv.id,
      );
    } catch {
      return undefined;
    }
  }
  if (conversationHasWrappedMetadata(conv)) return undefined;
  try {
    return decryptMemberSettings(conv.encryptedMemberSettings, conv.memberSettingsNonce, conv.id);
  } catch {
    return undefined;
  }
}

export function decryptConversationReadStateForDisplay(
  conversationId: string,
  encryptedBlob: string | undefined,
  metadataKey?: Uint8Array,
  _hasWrappedMetadataKeys = false,
): string | null {
  if (!encryptedBlob) return null;
  if (metadataKey) {
    const keyed = decryptReadStateWithKey(metadataKey, encryptedBlob, conversationId);
    if (keyed) return keyed;
  }
  // Leftover HKDF blob: read-state is per-identity obfuscation. Keyed blobs
  // fail HKDF; leftover pointers must still round-trip after a hard refresh
  // even if this device cannot unwrap the conversation metadata key.
  return decryptReadState(conversationId, encryptedBlob);
}

/**
 * True when `lastRead` is at or past `lastMessageId`. Exact equality covers
 * the normal pointer; ObjectId order covers a pointer that advanced past a
 * stale listed last (e.g. a system row the conversation doc never stored).
 */
export function readPointerCoversLastMessage(
  lastRead: string | null,
  lastMessageId: string,
): boolean {
  if (!lastRead) return false;
  if (lastRead === lastMessageId) return true;
  if (!isValidObjectIdHex(lastRead) || !isValidObjectIdHex(lastMessageId)) return false;
  return compareObjectIdHex(lastRead, lastMessageId) >= 0;
}

export function preferredReadPointer(input: {
  listedLastMessageId?: string;
  newestVisibleMessageId?: string;
  hasNewerPages?: boolean;
  existingReadPointer?: string | null;
}): string | undefined {
  // Detached from the live tail: unseen newer rows must not be marked read.
  // Keep the existing pointer when we have one; otherwise persist nothing.
  if (input.hasNewerPages) return input.existingReadPointer ?? undefined;
  const listed = input.listedLastMessageId;
  const newest = input.newestVisibleMessageId;
  if (!listed) return newest;
  if (!newest) return listed;
  if (
    isValidObjectIdHex(listed) &&
    isValidObjectIdHex(newest) &&
    compareObjectIdHex(newest, listed) > 0
  ) {
    return newest;
  }
  return listed;
}

/**
 * True when `nextMessageId` may replace a previously persisted pointer.
 * Equal ids are allowed (forced rewrite); older ObjectIds are not.
 */
export function shouldAdvancePersistedReadPointer(
  lastMessageId: string | undefined,
  nextMessageId: string,
): boolean {
  if (!lastMessageId || lastMessageId === nextMessageId) return true;
  if (!isValidObjectIdHex(lastMessageId) || !isValidObjectIdHex(nextMessageId)) return true;
  return compareObjectIdHex(nextMessageId, lastMessageId) >= 0;
}

/**
 * Serializes per-conversation last-read writes and drops older ids so a late
 * response cannot replace a newer pointer already accepted for that thread.
 */
export function createReadPointerPersistQueue(): {
  clear: () => void;
  persist: (
    conversationId: string,
    messageId: string,
    options: { force?: boolean } | undefined,
    write: () => Promise<string | null>,
  ) => Promise<string | null>;
} {
  const lastByConversation = new Map<string, string>();
  const chainByConversation = new Map<string, Promise<void>>();

  return {
    clear() {
      lastByConversation.clear();
      chainByConversation.clear();
    },
    async persist(conversationId, messageId, options, write) {
      const previous = chainByConversation.get(conversationId) ?? Promise.resolve();
      const run = async (): Promise<string | null> => {
        const last = lastByConversation.get(conversationId);
        if (!options?.force && last === messageId) return null;
        if (!shouldAdvancePersistedReadPointer(last, messageId)) return null;
        const blob = await write();
        if (!blob) return null;
        lastByConversation.set(conversationId, messageId);
        return blob;
      };
      const next = previous.then(run);
      chainByConversation.set(
        conversationId,
        next.then(
          () => undefined,
          () => undefined,
        ),
      );
      return next;
    },
  };
}

/**
 * Persist last-read as a keyed blob when this device holds the metadata key.
 * HKDF is used only when no metadata key is present so a later refresh can
 * still decrypt leftover pointers. A failed keyed write is not replaced with
 * HKDF. Returns the stored ciphertext, or null when the API write failed.
 */
export async function persistConversationReadPointer(input: {
  conversationId: string;
  messageId: string;
  metadataKey?: Uint8Array | null;
  updatePreferences: (
    conversationId: string,
    patch: { encryptedReadState: string },
  ) => Promise<{ success: boolean }>;
}): Promise<string | null> {
  const write = async (encryptedReadState: string): Promise<string | null> => {
    const resp = await input.updatePreferences(input.conversationId, { encryptedReadState });
    return resp.success ? encryptedReadState : null;
  };
  if (input.metadataKey) {
    return write(
      encryptReadStateWithKey(input.metadataKey, input.messageId, input.conversationId),
    );
  }
  return write(encryptReadState(input.conversationId, input.messageId));
}

/**
 * When leaving a conversation, persist that thread's listed last message as
 * the read pointer. Send and live viewing can advance lastMessageId after open
 * without rewriting encryptedReadState; a later hard refresh would otherwise
 * mark it unread unless it is focused (`isActive`).
 */
export function readPointerToPersistOnDeactivate(
  previousId: string | null,
  nextId: string | null,
  previousLastMessageId?: string,
): { conversationId: string; messageId: string } | null {
  if (!previousId || previousId === nextId || !previousLastMessageId) return null;
  return { conversationId: previousId, messageId: previousLastMessageId };
}

/**
 * Customer-facing unread rule: the encrypted read pointer must cover
 * `lastMessageId`. Leftover HKDF blobs still count as read, including when
 * wraps exist and this device has not unwrapped the metadata key.
 */
export function conversationHasUnreadFromReadState(input: {
  conversationId: string;
  lastMessageId?: string;
  encryptedReadState?: string;
  metadataKey?: Uint8Array;
  hasWrappedMetadataKeys: boolean;
  isActive: boolean;
  previousHasUnread?: boolean;
}): boolean {
  if (input.isActive) return false;
  if (!input.lastMessageId) return input.previousHasUnread ?? false;
  const lastRead = decryptConversationReadStateForDisplay(
    input.conversationId,
    input.encryptedReadState,
    input.metadataKey,
    input.hasWrappedMetadataKeys,
  );
  return !readPointerCoversLastMessage(lastRead, input.lastMessageId);
}

/**
 * Resolve a system event for display. Encrypted blobs decrypt with the metadata
 * key; leftover plaintext names on those rows are ignored if decrypt fails.
 * Legacy rows with no ciphertext still render their stored names.
 */
export function decryptSystemEventForDisplay(
  message: Pick<PublicMessage, 'systemEvent' | 'encryptedSystemEvent' | 'systemEventNonce'>,
  conversationId: string,
  metadataKey?: Uint8Array,
): SystemEvent | undefined {
  const { encryptedSystemEvent, systemEventNonce, systemEvent } = message;
  if (encryptedSystemEvent && systemEventNonce) {
    if (metadataKey) {
      try {
        return decryptSystemEvent(
          encryptedSystemEvent,
          systemEventNonce,
          conversationId,
          metadataKey,
        );
      } catch {
        return systemEvent ? stubSystemEvent(systemEvent) : undefined;
      }
    }
    return systemEvent ? stubSystemEvent(systemEvent) : undefined;
  }
  return systemEvent;
}

export function tryEncryptSystemEvent(
  event: SystemEvent,
  conversationId: string,
  metadataKey: Uint8Array | null | undefined,
): { encryptedSystemEvent: string; systemEventNonce: string } | undefined {
  if (!metadataKey) return undefined;
  try {
    return encryptSystemEvent(event, conversationId, metadataKey);
  } catch {
    return undefined;
  }
}

function wrapTargetKey(entry: SerializedWrappedKey): string {
  return `${entry.identityId}:${entry.routingTag ?? ''}`;
}

function wrapTargetsEqual(
  a: readonly SerializedWrappedKey[],
  b: readonly SerializedWrappedKey[],
): boolean {
  if (a.length !== b.length) return false;
  const sb = new Set(b.map(wrapTargetKey));
  for (const entry of a) {
    if (!sb.has(wrapTargetKey(entry))) return false;
  }
  return true;
}

function recoverNamePlaintext(
  conv: PublicConversation,
  key: Uint8Array,
): { plaintext: string | undefined; aadOk: boolean } {
  if (!conv.encryptedName || !conv.nameNonce) return { plaintext: undefined, aadOk: true };
  try {
    return {
      plaintext: decryptGroupNameWithKey(conv.encryptedName, conv.nameNonce, conv.id, key),
      aadOk: true,
    };
  } catch {
    try {
      return { plaintext: decryptGroupName(conv.encryptedName, conv.nameNonce, conv.id), aadOk: false };
    } catch {
      return { plaintext: undefined, aadOk: false };
    }
  }
}

function recoverSettingsPlaintext(
  conv: PublicConversation,
  key: Uint8Array,
): { plaintext: MemberSettingsMap | undefined; aadOk: boolean } {
  if (!conv.encryptedMemberSettings || !conv.memberSettingsNonce) {
    return { plaintext: undefined, aadOk: true };
  }
  try {
    return {
      plaintext: decryptMemberSettingsWithKey(
        conv.encryptedMemberSettings,
        conv.memberSettingsNonce,
        conv.id,
        key,
      ),
      aadOk: true,
    };
  } catch {
    try {
      return {
        plaintext: decryptMemberSettings(
          conv.encryptedMemberSettings,
          conv.memberSettingsNonce,
          conv.id,
        ),
        aadOk: false,
      };
    } catch {
      return { plaintext: undefined, aadOk: false };
    }
  }
}

export interface ConversationMetadataApi {
  updateName(
    conversationId: string,
    encryptedName: string,
    nameNonce: string,
    wrappedMetadataKeys?: SerializedWrappedKey[],
    options?: { ciphertextRotation?: boolean },
  ): Promise<{ success: boolean; data?: PublicConversation }>;
  updateMemberSettings(
    conversationId: string,
    encryptedMemberSettings: string,
    memberSettingsNonce: string,
    wrappedMetadataKeys?: SerializedWrappedKey[],
  ): Promise<{ success: boolean; data?: PublicConversation }>;
  updateMetadataKeys(
    conversationId: string,
    wrappedMetadataKeys: SerializedWrappedKey[],
  ): Promise<{ success: boolean; data?: PublicConversation }>;
  updatePreferences(
    conversationId: string,
    patch: { encryptedReadState?: string },
  ): Promise<{ success: boolean }>;
}

export interface EnsureConversationMetadataInput {
  conversation: PublicConversation;
  identityId: string;
  ecdhPrivateKey: Uint8Array | null;
  kemPrivateKey: Uint8Array | null;
  routingTag?: string;
  wrapableRecipients: RecipientKeys[];
  cache: Map<string, Uint8Array>;
  api: ConversationMetadataApi;
  encryptedReadState?: string;
  lastReadMessageId?: string;
  /** Group name/settings writes are admin-only; skip HKDF migration if we cannot persist them. */
  canPersistName?: boolean;
  canPersistSettings?: boolean;
}

export interface EnsureConversationMetadataResult {
  key: Uint8Array | null;
  conversation?: PublicConversation;
  wrappedMetadataKeys?: SerializedWrappedKey[];
}

/**
 * Unwrap or create the conversation metadata key, wrap to attested devices,
 * and re-encrypt leftover HKDF blobs so they are no longer server-readable.
 */
export async function ensureConversationMetadataKey(
  input: EnsureConversationMetadataInput,
): Promise<EnsureConversationMetadataResult> {
  const {
    conversation,
    identityId,
    ecdhPrivateKey,
    kemPrivateKey,
    routingTag,
    wrapableRecipients,
    cache,
    api,
    encryptedReadState,
    lastReadMessageId,
  } = input;
  const canPersistName = input.canPersistName !== false;
  const canPersistSettings = input.canPersistSettings !== false;
  const existingWraps = conversation.wrappedMetadataKeys ?? [];

  let key: Uint8Array | null = null;
  if (existingWraps.length > 0 && ecdhPrivateKey && kemPrivateKey) {
    key = unwrapConversationMetadataKey(
      existingWraps,
      identityId,
      ecdhPrivateKey,
      kemPrivateKey,
      routingTag,
    );
  }
  if (!key) {
    key = cache.get(conversation.id) ?? null;
  }

  let generated = false;
  if (!key) {
    if (existingWraps.length > 0) {
      return { key: null };
    }
    const wouldRewriteName = !!(conversation.encryptedName && conversation.nameNonce);
    const wouldRewriteSettings = !!(
      conversation.encryptedMemberSettings && conversation.memberSettingsNonce
    );
    if ((wouldRewriteName && !canPersistName) || (wouldRewriteSettings && !canPersistSettings)) {
      return { key: null };
    }
    const candidate = generateConversationMetadataKey();
    const trialWraps = wrapConversationMetadataKey(candidate, wrapableRecipients);
    if (trialWraps.length === 0) {
      return { key: null };
    }
    key = candidate;
    generated = true;
  }

  cache.set(conversation.id, key);

  const wrapsOut = wrapConversationMetadataKey(key, wrapableRecipients);
  if (wrapsOut.length === 0) {
    return { key, wrappedMetadataKeys: existingWraps, conversation };
  }

  const wrapTargetsChanged = !wrapTargetsEqual(wrapsOut, existingWraps);
  const persistWraps = generated || wrapTargetsChanged;

  const nameRecovered = recoverNamePlaintext(conversation, key);
  const settingsRecovered = recoverSettingsPlaintext(conversation, key);
  const rewriteName =
    !!nameRecovered.plaintext && (generated || !nameRecovered.aadOk) && canPersistName;
  const rewriteSettings =
    !!settingsRecovered.plaintext && (generated || !settingsRecovered.aadOk) && canPersistSettings;

  const keyedRead = encryptedReadState
    ? decryptReadStateWithKey(key, encryptedReadState, conversation.id)
    : null;
  const hkdfRead = encryptedReadState
    ? decryptReadState(conversation.id, encryptedReadState)
    : null;
  const readPlain = lastReadMessageId ?? keyedRead ?? hkdfRead ?? undefined;
  const rewriteRead =
    !!readPlain && (generated || lastReadMessageId != null || (!keyedRead && !!hkdfRead));

  let latest: PublicConversation | undefined;

  if (rewriteName && nameRecovered.plaintext) {
    const encrypted = encryptGroupName(nameRecovered.plaintext, conversation.id, key);
    const resp = await api.updateName(
      conversation.id,
      encrypted.encryptedName,
      encrypted.nameNonce,
      persistWraps ? wrapsOut : undefined,
      { ciphertextRotation: true },
    );
    if (resp.success && resp.data) latest = resp.data;
  }

  if (rewriteSettings && settingsRecovered.plaintext) {
    const encrypted = encryptMemberSettings(settingsRecovered.plaintext, conversation.id, key);
    const resp = await api.updateMemberSettings(
      conversation.id,
      encrypted.encryptedMemberSettings,
      encrypted.memberSettingsNonce,
      persistWraps ? wrapsOut : undefined,
    );
    if (resp.success && resp.data) latest = resp.data;
  }

  if (persistWraps && !rewriteName && !rewriteSettings) {
    const resp = await api.updateMetadataKeys(conversation.id, wrapsOut);
    if (resp.success && resp.data) latest = resp.data;
  }

  if (rewriteRead && readPlain) {
    const blob = encryptReadStateWithKey(key, readPlain, conversation.id);
    await api.updatePreferences(conversation.id, { encryptedReadState: blob });
  }

  return {
    key,
    conversation: latest,
    wrappedMetadataKeys: wrapsOut,
  };
}
